import { FC, ReactNode, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import Text from '../text';
import { useQuery } from 'react-query';
import useThread from '@/hooks/use-thread.hook';
import useThreadHistory from '@/hooks/use-thread-history.hook';
import { getAllThreads } from '@/lib/services/thread.service';
import { cloneDeep, debounce, flatten } from 'lodash';
import { bucketByDate } from '@/lib/date-helpers';
import SidebarList from './sidebar-list';
import { QueryKey } from '@/lib/query-client';
import useThreadToast from '@/hooks/use-thread-toast.hook';
import { ThreadModel } from '@/lib/models/thread/thread.model';
import Tab from './tab';
import TabHeader from './tab/tab-header';
import TabContent from './tab/tab-content';
import SidebarSearch from './sidebar-list/sidebar-search';
import { MenuItemConfig } from '../menu';
import { SidebarListItemProps } from './sidebar-list/helpers';
import { IconType } from '../icon/helpers';
import { useNavigate } from '@tanstack/react-router';

interface Props {
  onLoadThread?: () => void;
  onClose?: () => void;
}

const DateBucketHeaders: { title: string; icon?: IconType }[] = [
  { title: 'Today' },
  { title: 'This Week', icon: 'calendar' },
  { title: 'All' },
];

const HistoryTab: FC<Props> = ({ onLoadThread, onClose }) => {
  const [keyword, setKeyword] = useState('');
  const [keywordVal, setKeywordVal] = useState('');
  const [threads, setThreads] = useState<ThreadModel[]>([]);

  const { loadThread, thread } = useThread();
  const navigate = useNavigate();
  const { clear: clearThreadToast } = useThreadToast();
  const {
    threadHistory,
    toggleArchived,
    togglePinned,
    isLoading: isThreadMetadataLoading,
  } = useThreadHistory();
  const activeThreadId = thread.id;

  const { data, isLoading } = useQuery([QueryKey.ThreadsList, keyword], async () =>
    getAllThreads(keyword)
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleChangeKeyword = useCallback(
    debounce((keyword: string) => {
      setKeyword(keyword);
    }, 600),
    []
  );

  const handleKeywordValChange = useCallback(
    (value: string) => {
      setKeywordVal(value);
      debounceHandleChangeKeyword(value);
    },
    [debounceHandleChangeKeyword]
  );

  const handleLoadThread = useCallback(
    (threadId: string) => {
      onLoadThread?.();
      loadThread(threadId).then((loaded) => {
        if (loaded) {
          clearThreadToast();
          navigate({ to: `/chat/${threadId}` });
        }
      });
    },
    [loadThread, clearThreadToast, onLoadThread, navigate]
  );

  const listItems = useMemo(() => {
    const validThreads = threads.filter(({ latestUserMessage }) => !!latestUserMessage);
    const dateBuckets = bucketByDate(validThreads, 'latestMessageTimestamp');
    const pinnedThreads = threadHistory.filter(({ status }) => status === 'pinned');
    const pinnedThreadIds = new Set(pinnedThreads.map(({ threadId }) => threadId));

    const archivedThreads = threadHistory.filter(({ status }) => status === 'archived');
    const archivedThreadIds = new Set(archivedThreads.map(({ threadId }) => threadId));

    const items: SidebarListItemProps[][] = [];

    const getListItemProps = (thread: ThreadModel, keySuffix = ''): SidebarListItemProps => {
      const { id: threadId, conversation, latestUserMessage } = thread;
      const { content, shortcut, context } = latestUserMessage!;

      const hasShortcut = !!shortcut;
      const isFile = context?.type === 'file';

      let blurb: ReactNode = content;
      if (hasShortcut) {
        blurb = `Shortcut: ${shortcut.name}`;
      } else if (isFile) {
        blurb = <>File Upload: {context?.name || <Text italic>not found</Text>}</>;
      }

      const history = threadHistory.find(
        ({ threadId: historyThreadId }) => historyThreadId === threadId
      );
      const isArchived = history?.status === 'archived';
      const isPinned = history?.status === 'pinned';

      const menuItems: MenuItemConfig[] = [
        {
          id: 'archive',
          label: isArchived ? 'Unarchive' : 'Archive',
          onClick: () => toggleArchived(threadId),
          icon: 'archive',
        },
        {
          id: 'pin',
          label: isPinned ? 'Unpin' : 'Pin',
          onClick: () => togglePinned(threadId),
          icon: isPinned ? 'unpin' : 'pin',
        },
      ];

      return {
        title: blurb,
        key: ['thread', threadId, conversation.length, keySuffix].filter((v) => v).join('-'),
        onClick: () => handleLoadThread(threadId),
        active: activeThreadId === threadId,
        menuItems,
      };
    };

    dateBuckets.forEach((bucketThreads, index) => {
      if (!bucketThreads.length) {
        return;
      }

      const bucketItems: SidebarListItemProps[] = [];

      bucketThreads.forEach((bucketThread) => {
        // pinned threads only appear in the pinned group
        if (pinnedThreadIds.has(bucketThread.id)) {
          return;
        }
        // archived threads are hidden
        if (archivedThreadIds.has(bucketThread.id)) {
          return;
        }

        bucketItems.push(getListItemProps(bucketThread));
      });

      if (bucketItems.length) {
        const { title, icon } = DateBucketHeaders[index];

        bucketItems.unshift({
          title,
          key: `date-bucket-${index}`,
          header: true,
          icon,
        });
      }

      items.push(bucketItems);
    });

    if (pinnedThreads.length) {
      const pinnedItems: SidebarListItemProps[] = [];

      pinnedThreads.forEach(({ threadId }) => {
        const pinnedThread = validThreads.find(({ id }) => id === threadId);
        if (pinnedThread) {
          pinnedItems.push(getListItemProps(pinnedThread, 'pinned'));
        }
      });

      if (pinnedItems.length) {
        pinnedItems.unshift({
          title: 'Pinned',
          key: 'pinned-bucket',
          header: true,
          icon: 'unpin',
        });

        items.unshift(pinnedItems);
      }
    }

    if (archivedThreads.length) {
      const archivedItems: SidebarListItemProps[] = [];

      archivedThreads.forEach(({ threadId }) => {
        const archivedThread = validThreads.find(({ id }) => id === threadId);
        if (archivedThread) {
          archivedItems.push(getListItemProps(archivedThread, 'archived'));
        }
      });

      if (archivedItems.length) {
        archivedItems.unshift({
          title: 'Archived',
          key: 'archived-bucket',
          header: true,
          expand: true,
          icon: 'archive',
        });

        items.push(archivedItems);
      }
    }

    return flatten(items);
  }, [activeThreadId, threads, threadHistory, toggleArchived, togglePinned, handleLoadThread]);

  useLayoutEffect(() => {
    if (!data || isLoading) {
      return;
    }

    setThreads(() => {
      if (!thread.latestUserMessage) {
        return data;
      }

      const updated = cloneDeep(data);
      const activeIndex = updated.findIndex(({ id }) => id === thread.id);

      if (activeIndex === -1) {
        updated.unshift(thread);
      } else {
        updated[activeIndex] = thread;
      }

      return updated;
    });
  }, [data, isLoading, thread]);

  const listLoading = isThreadMetadataLoading || isLoading;

  return (
    <Tab onClose={onClose}>
      <TabHeader
        icon="history"
        title="History"
        description="History includes all conversations up to the last 30 days."
      />
      <TabContent>
        <SidebarSearch
          name="history-search"
          value={keywordVal}
          onChange={handleKeywordValChange}
          placeholder="Search history"
        />
      </TabContent>
      <SidebarList
        isLoading={listLoading}
        items={listItems}
        emptyMessage="No conversations found"
      />
    </Tab>
  );
};

export default HistoryTab;
